import { useRequiredContext } from "@redotech/react-util/context";
import { AuthContext } from "@redotech/redo-merchant-app-common/auth";
import { PermissionsContext } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  MerchantRoutes,
  MerchantRoutesContext,
} from "@redotech/redo-merchant-app-common/merchant-routes";
import { isSupportTicketRouteId } from "@redotech/redo-merchant-app-common/routing/links";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  findDefaultOutboundView,
  OutboundViewsContext,
} from "@redotech/redo-merchant-fulfillment-app/outbound-views";
import {
  OrderManagementPermissions,
  ReturnsAndClaimsPermissions,
} from "@redotech/redo-model/permissions";
import { ManagedUserRoles } from "@redotech/redo-model/roles";
import { OutboundSystemView } from "@redotech/redo-model/views/default-outbound-views";
import { memo, useContext, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { NavigationVersionContext } from "../app/navigation/navigation-version-context";
import { OrderManagementSuiteStatusesContext } from "./order-management-cloud";
import { SupportSuiteStatusesContext } from "./support-cloud";

export const MerchantRouteNavigate = memo(function MerchantRoute({
  routeTo,
}: {
  routeTo: (routes: MerchantRoutes) => string;
}) {
  const navigate = useNavigate();
  const routes = useContext(MerchantRoutesContext);

  useEffect(() => {
    if (!routes) {
      return;
    }
    const href = routeTo(routes);
    navigate(href, { replace: true });
  }, [navigate, routes, routeTo]);

  return null;
});

/**
 * Decides where a user lands when they open the Shipping section.
 *
 * Admins land on the analytics dashboard. Everyone else is dropped directly
 * into the "Ready to ship" fulfillment group (the awaiting-shipment view),
 * which also auto-expands the Fulfillment nav group since it's a fulfillment
 * child route. Permission-based fallbacks are preserved: teams without OMS
 * see the marketing/upsell page, and scan-only warehouse users still land on
 * the scan page.
 */
export function getShippingDefaultRoute({
  shippingBaseUrl,
  omsEnabled,
  isAdmin,
  canViewDashboard,
  canManageShipments,
  canScanAndVerify,
  readyToShipViewName,
}: {
  shippingBaseUrl: string;
  omsEnabled: boolean;
  isAdmin: boolean;
  canViewDashboard: boolean;
  canManageShipments: boolean;
  canScanAndVerify: boolean;
  readyToShipViewName: string;
}): string {
  const aboutRoute = `${shippingBaseUrl}/outbound-labels/about`;
  const dashboardRoute = `${shippingBaseUrl}/outbound-labels/dashboard`;
  // The "Ready to ship" landing is whichever fulfillment-group view the team
  // uses as their default. Teams can rename the system awaiting-shipment view,
  // so the slug is resolved from the team's actual views at the caller; we
  // just URL-encode it here. Landing on this view also auto-expands the
  // Fulfillment nav group since it's a fulfillment child route.
  const readyToShipRoute = `${shippingBaseUrl}/outbound-labels/${encodeURIComponent(readyToShipViewName)}`;
  const scanRoute = `${shippingBaseUrl}/outbound-labels/scan`;

  // When OMS is not enabled, land on the marketing/upsell page instead of the
  // (empty) fulfillment dashboard. Teams that have returns enabled can still
  // reach Carrier claims via the secondary nav.
  if (!omsEnabled) {
    return aboutRoute;
  }
  // Admins land on the analytics dashboard. Non-admins are dropped directly
  // into the "Ready to ship" fulfillment group instead.
  if (isAdmin && canViewDashboard) {
    return dashboardRoute;
  }
  if (canManageShipments) {
    return readyToShipRoute;
  }
  if (canScanAndVerify) {
    return scanRoute;
  }
  return readyToShipRoute;
}

export const MerchantRoutesProvider = memo(function MerchantRoutesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useContext(AuthContext);
  const team = useContext(TeamContext);
  const user = useContext(UserContext);

  const isAdmin =
    user?.managedUserRoles?.includes(ManagedUserRoles.ADMIN) ?? false;

  const { omsEnabled, warrantiesEnabled } = useContext(
    OrderManagementSuiteStatusesContext,
  );
  const { supportV2Enabled } = useContext(SupportSuiteStatusesContext) ?? {};
  const permissions = useContext(PermissionsContext);
  const { newNavigationEnabled } = useContext(NavigationVersionContext);
  const outboundViews = useContext(OutboundViewsContext);

  // Drop non-admins onto whichever fulfillment-group view sits visually at
  // the top of the team's nav — folders and top-level views together by
  // position, diving into the first folder if one sorts above any top-level
  // view. Matches what the merchant actually sees, so teams who rename,
  // reorder, or replace the system awaiting-shipment view still land
  // somewhere real instead of the create-view editor. While views are still
  // loading, fall back to the system slug — once views resolve the nav href
  // updates.
  const readyToShipViewName = useMemo(() => {
    if (!outboundViews?.views || outboundViews.views.length === 0) {
      return OutboundSystemView.AWAITING_SHIPMENT;
    }
    const first = findDefaultOutboundView({
      views: outboundViews.views,
      folders: outboundViews.folders ?? [],
    });
    return first?.name ?? OutboundSystemView.AWAITING_SHIPMENT;
  }, [outboundViews]);

  const teamId = auth?.teamId;

  const baseUrl = teamId ? `/stores/${teamId}` : "";
  const storeBaseUrl = baseUrl;
  const homeBaseHref = `${storeBaseUrl}/home`;
  // When the new-navigation flag is OFF, `shippingBaseUrl` and
  // `returnsBaseUrl` collapse to `${baseUrl}/order-management`. That single
  // root is where all legacy Operations pages live, so every derived href
  // below automatically resolves to the correct legacy URL without any
  // additional branching. When the flag is ON, they split into the new
  // `/shipping` and `/returns` trees.
  const orderManagementBaseUrl = `${baseUrl}/order-management`;
  const shippingBaseUrl = newNavigationEnabled
    ? `${baseUrl}/shipping`
    : orderManagementBaseUrl;
  const returnsBaseUrl = newNavigationEnabled
    ? `${baseUrl}/returns`
    : orderManagementBaseUrl;
  const inventoryManagementBaseUrl = `${baseUrl}/inventory`;
  const marketingBaseUrl = `${baseUrl}/marketing`;
  const legacySupportBaseUrl = `${baseUrl}/support`;
  const supportV2BaseUrl = `${baseUrl}/support-v2`;
  const supportBaseUrl = supportV2Enabled
    ? supportV2BaseUrl
    : legacySupportBaseUrl;
  const conversionBaseUrl = `${baseUrl}/conversion`;
  const customersBaseUrl = `${baseUrl}/customer`;
  const financeBaseUrl = `${baseUrl}/finance`;
  const redoSuppliesBaseUrl = `${baseUrl}/redo-supplies`;
  const aeoBaseUrl = `${baseUrl}/aeo`;
  const developerBaseUrl = `${baseUrl}/developer`;

  const claimsHref = `${returnsBaseUrl}/claims`;

  const shippingDefaultRoute = useMemo(
    () =>
      getShippingDefaultRoute({
        shippingBaseUrl,
        omsEnabled: !!omsEnabled,
        isAdmin,
        canViewDashboard: !!permissions?.has(
          OrderManagementPermissions.VIEW_ORDER_FULFILLMENT_ANALYTICS,
        ),
        canManageShipments: !!permissions?.has(
          OrderManagementPermissions.MANAGE_SHIPMENTS,
        ),
        canScanAndVerify: !!permissions?.has(
          OrderManagementPermissions.SCAN_AND_VERIFY,
        ),
        readyToShipViewName,
      }),
    [permissions, omsEnabled, shippingBaseUrl, isAdmin, readyToShipViewName],
  );

  const returnsDefaultRoute = useMemo(() => {
    const canViewReturns = permissions?.has(
      ReturnsAndClaimsPermissions.VIEW_RETURN,
    );
    const canManageWarranties = permissions?.has(
      ReturnsAndClaimsPermissions.MANAGE_WARRANTY,
    );

    if (canViewReturns) {
      return `${returnsBaseUrl}/returns`;
    } else if (canManageWarranties && warrantiesEnabled) {
      return `${returnsBaseUrl}/warranties`;
    }
    return `${returnsBaseUrl}/returns`;
  }, [permissions, warrantiesEnabled, returnsBaseUrl]);

  const claimsDefaultRoute = useMemo(() => {
    const canManagePackageProtectionClaims = permissions?.has(
      ReturnsAndClaimsPermissions.MANAGE_PACKAGE_PROTECTION_CLAIM,
    );

    const canManageCarrierClaims = permissions?.has(
      ReturnsAndClaimsPermissions.MANAGE_CARRIER_CLAIM,
    );

    const packageProtectionEnabled =
      !!team?.settings.packageProtection?.enabled;

    const omsEnabled = !!team?.settings.outboundLabels?.newOmsEnabled;
    const isReturnEnabled = team?.settings.returns?.enabled;

    if (canManagePackageProtectionClaims && packageProtectionEnabled) {
      return `${claimsHref}/all`;
    } else if (canManageCarrierClaims && (omsEnabled || isReturnEnabled)) {
      return `${claimsHref}/carrier-claims`;
    } else {
      return `${claimsHref}/all`;
    }
  }, [team?.settings, claimsHref, permissions]);

  const merchantRoutes: MerchantRoutes = useMemo(
    () => ({
      // Home routes
      overviewHref: `${homeBaseHref}/overview`,
      analyticsHref: `${homeBaseHref}/analytics`,
      getAnalyticsSubRoute: (subPath: string) =>
        `${homeBaseHref}/analytics/${subPath}`,
      homeBaseHref,

      // Shipping routes
      shippingDefaultRoute,
      pickSessionHref: `${shippingBaseUrl}/outbound-labels/pick-sessions`,
      pickSessionTableHref: `${shippingBaseUrl}/outbound-labels/admin-pick-sessions`,
      transferHref: `${shippingBaseUrl}/warehouse/transfer`,
      instantTransferHref: `${shippingBaseUrl}/warehouse/instant-transfer`,
      receiveToBinHref: `${shippingBaseUrl}/warehouse/receive-to-bin`,
      consolidationHref: `${shippingBaseUrl}/warehouse/consolidation`,
      flushHref: `${shippingBaseUrl}/warehouse/flush`,

      ordersOutboundLabelsHref: `${shippingBaseUrl}/outbound-labels`,
      getOrdersOutboundLabelsSubRoute: (subPath: string) =>
        `${shippingBaseUrl}/outbound-labels/${subPath}`,

      warehouseHref: `${shippingBaseUrl}/warehouse`,
      warehouseBaseUrl: `${shippingBaseUrl}/warehouse`,

      orderEditsHref: `${shippingBaseUrl}/outbound-labels/order-edits`,
      analyticsOrderFulfillmentHref: `${shippingBaseUrl}/analytics/order-fulfillment`,
      getAnalyticsOrderFulfillmentSubRoute: (subPath: string) =>
        `${shippingBaseUrl}/analytics/order-fulfillment?tab=${subPath}`,
      shippingCarrierClaimsHref: `${shippingBaseUrl}/carrier-claims`,

      // Returns routes
      returnsDefaultRoute,
      returnsHref: `${returnsBaseUrl}/returns`,
      getReturnsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/returns/${subPath}`,

      claimsHref,
      claimsDefaultRoute,
      getClaimsSubRoute: (subPath: string) => `${claimsHref}/${subPath}`,

      getWarrantiesHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${returnsBaseUrl}/warranties/all-warranties`
          : `${returnsBaseUrl}/warranties/about`,
      warrantiesHref: `${returnsBaseUrl}/warranties`,
      getWarrantiesSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/warranties/${subPath}`,
      registrationsHref: `${returnsBaseUrl}/registrations`,

      returnsProcessingHref: `${orderManagementBaseUrl}/warehouse/processing`,

      getRecycleHref: () => `${returnsBaseUrl}/recycle/all-recycle`,
      recycleHref: `${returnsBaseUrl}/recycle`,
      getRecycleSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/recycle/${subPath}`,

      getWithdrawalHref: () => `${returnsBaseUrl}/withdrawals/all-withdrawals`,
      withdrawalHref: `${returnsBaseUrl}/withdrawals`,
      getWithdrawalSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/withdrawals/${subPath}`,

      fraudHref: `${returnsBaseUrl}/fraud`,
      getFraudSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/fraud/${subPath}`,

      analyticsReturnsClaimsHref: `${returnsBaseUrl}/analytics/returns-claims`,
      getAnalyticsReturnsClaimsSubRoute: (subPath: string) =>
        `${returnsBaseUrl}/analytics/returns-claims/${subPath}`,
      analyticsReportsHref: `${returnsBaseUrl}/analytics/reports`,
      analyticsManagedReturnsHref: `${returnsBaseUrl}/analytics/managed-returns`,
      warehouseSlasHref: `${shippingBaseUrl}/wms/warehouse-slas`,
      processingPipelineHref: `${shippingBaseUrl}/wms/processing-pipeline`,

      // Inventory routes
      getInventoryHref: ({ enabled }: { enabled: boolean }) =>
        `${inventoryManagementBaseUrl}`,

      inventoryProductsHref: `${inventoryManagementBaseUrl}/catalog/products`,

      // Marketing routes
      marketingAnalyticsHref: `${marketingBaseUrl}/analytics`,
      marketingAnalyticsReportsHref: `${marketingBaseUrl}/analytics/reports`,
      automationsHref: `${marketingBaseUrl}/automations`,
      marketingSpotlightsHref: `${marketingBaseUrl}/spotlights`,
      getMarketingSpotlightsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/spotlights/${subPath}`,

      getTrackingHref: ({ enabled }: { enabled: boolean }) =>
        enabled
          ? `${marketingBaseUrl}/tracking`
          : `${marketingBaseUrl}/tracking/about`,

      getTrackingSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/tracking/${subPath}`,

      recoverHref: `${marketingBaseUrl}/recover`,
      getRecoverSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/recover/${subPath}`,

      contextualHref: `${marketingBaseUrl}/contextual`,
      getContextualSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/contextual/${subPath}`,

      reviewsHref: `${marketingBaseUrl}/reviews/dashboard`,
      getReviewsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/reviews/${subPath}`,

      emailSmsHref: `${marketingBaseUrl}/email-sms`,
      getEmailSmsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/email-sms/${subPath}`,

      discountsHref: `${marketingBaseUrl}/discounts`,
      getDiscountsSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/discounts/${subPath}`,

      loyaltyHref: `${marketingBaseUrl}/loyalty/program`,
      getLoyaltySubRoute: (subPath: string) =>
        `${marketingBaseUrl}/loyalty/${subPath}`,

      marketingAgentHref: `${marketingBaseUrl}/marketing-agent`,
      getMarketingAgentSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/marketing-agent/${subPath}`,

      shopperAgentHref: `${baseUrl}/redo-concierge`,
      getShopperAgentSubRoute: (subPath: string) =>
        `${baseUrl}/redo-concierge/${subPath}`,

      textConciergeHref: `${marketingBaseUrl}/text-concierge`,
      getTextConciergeSubRoute: (subPath: string) =>
        `${marketingBaseUrl}/text-concierge/${subPath}`,

      // Support routes
      getSupportHref: ({ enabled }: { enabled: boolean }) => {
        if (!enabled) {
          return `${legacySupportBaseUrl}/about`;
        }
        if (supportV2Enabled) {
          return supportV2BaseUrl;
        }
        return `${legacySupportBaseUrl}/table/all`;
      },

      getSupportTableHref: (viewName: string) => {
        return `${legacySupportBaseUrl}/table/${encodeURIComponent(viewName)}`;
      },
      getSupportTableSubRoute: (subPath: string) => {
        return `${legacySupportBaseUrl}/table/${subPath}`;
      },
      getConversationHref: (conversationId: string) => {
        if (supportV2Enabled && isSupportTicketRouteId(conversationId)) {
          return `${supportV2BaseUrl}/${conversationId}`;
        }
        return `${legacySupportBaseUrl}/table/all?activeConversationId=${conversationId}`;
      },
      getConciergeHref: ({ enabled }: { enabled: boolean }) => {
        return enabled
          ? `${legacySupportBaseUrl}/dashboard`
          : `${legacySupportBaseUrl}/concierge/preview`;
      },
      getConciergeSubRoute: (subPath: string) => {
        return `${legacySupportBaseUrl}/concierge/${subPath}`;
      },

      getSupportDevSubRoute: (subPath: string) => {
        return `${legacySupportBaseUrl}/dev/${subPath}`;
      },

      // Conversion routes
      spotlightsHref: `${conversionBaseUrl}/spotlights`,
      getSpotlightsSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/spotlights/${subPath}`,
      conversionAnalyticsHref: `${conversionBaseUrl}/analytics`,
      conversionAnalyticsReportsHref: `${conversionBaseUrl}/analytics/reports`,
      getConversionHref: ({
        enabled,
        checkoutOutboundRatesEnabled,
      }: {
        enabled: boolean;
        checkoutOutboundRatesEnabled: boolean;
      }) => {
        if (enabled && checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/dashboard`;
        } else if (enabled && !checkoutOutboundRatesEnabled) {
          return `${conversionBaseUrl}/onboard`;
        } else {
          return `${conversionBaseUrl}/welcome`;
        }
      },

      // Marketing routes
      getMarketingHref: ({
        emailSmsEnabled,
        marketingCloudEnabled,
      }: {
        emailSmsEnabled: boolean;
        marketingCloudEnabled: boolean;
      }) => {
        if (emailSmsEnabled || marketingCloudEnabled) {
          return marketingBaseUrl;
        }
        return `${marketingBaseUrl}/welcome`;
      },

      getSegmentBuilderRoute({
        teamId,
        segmentId,
      }: {
        teamId: string;
        segmentId: string;
      }) {
        return `${storeBaseUrl}/marketing/segments/${segmentId}`;
      },

      getCustomerRecordHref: ({ customerId }: { customerId?: string }) =>
        customerId
          ? `${customersBaseUrl}/${customerId}`
          : `${customersBaseUrl}/all-customers`,

      getConversionSubRoute: (subPath: string) =>
        `${conversionBaseUrl}/${subPath}`,

      // Settings routes
      settingsHref: `${storeBaseUrl}/settings`,
      getSettingsSubRoute: (subPath: string) =>
        `${storeBaseUrl}/settings/${subPath}`,

      // Base URL helpers
      shippingBaseUrl,
      returnsBaseUrl,
      inventoryManagementBaseUrl,
      marketingBaseUrl,
      legacySupportBaseUrl,
      supportBaseUrl,
      conversionBaseUrl,
      storeBaseUrl,
      customersBaseUrl,
      aeoBaseUrl,

      // Finance routes
      financeBaseUrl,
      disputesHref: `${financeBaseUrl}/disputes`,
      disputesDashboardHref: `${financeBaseUrl}/disputes/dashboard`,
      disputesChargebacksHref: `${financeBaseUrl}/disputes/chargebacks`,
      disputesAlertsHref: `${financeBaseUrl}/disputes/alerts`,
      disputesSettingsHref: `${financeBaseUrl}/disputes/settings`,
      disputesIntegrationsHref: `${financeBaseUrl}/disputes/integrations`,

      // Redo Supplies routes
      redoSuppliesBaseUrl,
      redoSuppliesShopHref: `${redoSuppliesBaseUrl}/shop`,
      redoSuppliesOrderHistoryHref: `${redoSuppliesBaseUrl}/order-history`,
      redoSuppliesRecurringPurchasesInventoryHref: `${redoSuppliesBaseUrl}/recurring-purchases/inventory`,
      redoSuppliesRecurringPurchasesTimeHref: `${redoSuppliesBaseUrl}/recurring-purchases/time`,

      // AEO routes
      aeoDashboardHref: `${aeoBaseUrl}/dashboard`,
      aeoIntegrationsHref: `${aeoBaseUrl}/integrations`,
      aeoPromptsHref: `${aeoBaseUrl}/prompts`,
      aeoCatalogHref: `${aeoBaseUrl}/catalog`,
      aeoAssistantHref: `${aeoBaseUrl}/assistant`,
      aeoCompetitorsHref: `${aeoBaseUrl}/competitors`,
      aeoBrandHref: `${aeoBaseUrl}/brand`,
      getAeoSubRoute: (subPath: string) => `${aeoBaseUrl}/${subPath}`,
      getAeoCatalogSubRoute: (subPath: string) =>
        `${aeoBaseUrl}/catalog/${subPath}`,

      // Developer portal routes
      developerBaseUrl,
      developerFunctionsHref: `${developerBaseUrl}/functions`,
      developerLogsHref: `${developerBaseUrl}/logs`,
    }),
    [
      homeBaseHref,
      shippingBaseUrl,
      returnsBaseUrl,
      inventoryManagementBaseUrl,
      marketingBaseUrl,
      aeoBaseUrl,
      developerBaseUrl,
      financeBaseUrl,
      redoSuppliesBaseUrl,
      legacySupportBaseUrl,
      supportV2BaseUrl,
      supportBaseUrl,
      conversionBaseUrl,
      customersBaseUrl,
      storeBaseUrl,
      baseUrl,
      supportV2Enabled,
      shippingDefaultRoute,
      returnsDefaultRoute,
      claimsHref,
      claimsDefaultRoute,
      orderManagementBaseUrl,
    ],
  );

  return (
    <MerchantRoutesContext.Provider value={merchantRoutes}>
      {children}
    </MerchantRoutesContext.Provider>
  );
});

// Convenience hook
export function useMerchantRoutes(): MerchantRoutes {
  return useRequiredContext(MerchantRoutesContext);
}
